import { useCallback, useEffect, useState } from "react";
import type { Scope } from "./db";
import { entries, type OutboxEntry, pausedUntil, RETRY_MS, sendAll, signInNeeded, subscribe, type Trigger, waiting } from "./outbox";
import { refreshVisitDefaults } from "./pages";

const senders = new Map<string, () => Promise<string>>();

/** Start a pass when the app has a sender for this user; `force` tries with no signal, and before the Retry-After time. */
export function kick(scope: Scope, force = false, trigger: Trigger = "button"): void {
  const token = senders.get(scope.prefix);
  if (!token || (!force && (!navigator.onLine || pausedUntil(scope) > Date.now()))) return;
  void sendAll(scope, token, Date.now, trigger).catch((error) => console.warn("outbox: a pass failed", error));
}

/** The kept entries, read again at each change. */
export function useOutbox(scope: Scope): { all: OutboxEntry[] | null; signIn: boolean } {
  const [all, setAll] = useState<OutboxEntry[] | null>(null);
  const [signIn, setSignIn] = useState(() => signInNeeded(scope));
  useEffect(() => {
    let live = true;
    const reload = () =>
      void entries(scope).then((found) => {
        if (!live) return;
        setAll(found);
        setSignIn(signInNeeded(scope));
      });
    reload();
    const stop = subscribe(scope, reload);
    return () => {
      live = false;
      stop();
    };
  }, [scope]);
  return { all, signIn };
}

/** Send at the start, after `online`, after each new entry, and each minute while entries wait. A sent start or finish keeps new visit defaults. */
export function useOutboxSender(scope: Scope, token: () => Promise<string>, on: boolean): () => void {
  const send = useCallback(() => kick(scope), [scope]);
  useEffect(() => {
    if (!on) return;
    senders.set(scope.prefix, token);
    kick(scope, false, "start");
    const online = () => kick(scope, false, "online");
    const stop = subscribe(scope, (change) => {
      if (change?.entry.state === "saved" && change.entry.tries === 0) kick(scope, false, "new");
      if (change?.entry.state === "sent" && ["start", "finish"].includes(change.entry.kind)) void refreshVisitDefaults(scope, token);
    });
    const timer = setInterval(() => {
      void entries(scope).then((found) => found.some(waiting) && kick(scope, false, "timer"));
    }, RETRY_MS);
    addEventListener("online", online);
    return () => {
      senders.delete(scope.prefix);
      stop();
      clearInterval(timer);
      removeEventListener("online", online);
    };
  }, [scope, token, on, send]);
  return send;
}
